import {provider, forEachIntent} from '../intent';
import type {Intent} from '../intent';
import {assignTokensIntent} from '../token/assignTokens';
import {themeIntent} from './intent';
import type {ThemeCustomizes, ThemeMetadata} from './types';
import {isTheme} from './utils';

/** Reads the metadata off a theme intent, or `undefined` if it is not a theme. */
export function getThemeMetadata(intent: Intent): ThemeMetadata | undefined {
  return themeIntent.isIntent(intent) ? intent.metadata : undefined;
}

/**
 * Walks a theme's intents to derive what it customizes:
 * - `tokens`: true if any intent is a token reassignment.
 * - `components`: the deduped names of every component config the theme
 *   overrides (component config intents are `provider` intents whose context
 *   is the component's config, tagged with `options.name`).
 *
 * `forEachIntent` flattens compound (`toIntent`) intents, so customizations
 * supplied through `toIntent([...])` are classified by their leaves. Theme and
 * application boundaries are visited explicitly so composed themes report the
 * union without discarding those boundaries during ordinary intent traversal.
 */
function derive(intents: Intent<unknown>[]): ThemeCustomizes {
  let tokens = false;
  const components = new Set<string>();

  const visit = (intent: Intent<unknown>) => {
    if (isTheme(intent)) {
      forEachIntent(intent.intents, visit);
    } else if (assignTokensIntent.isIntent(intent)) {
      tokens = true;
    } else if (provider.isIntent(intent)) {
      const [context] = intent as unknown as [
        {options?: {name?: string}},
        unknown,
      ];
      const name = context?.options?.name;
      if (typeof name === 'string') {
        components.add(name);
      }
    }
  };
  forEachIntent(intents, visit);

  return {tokens, components: [...components]};
}

/**
 * Creates a theme: a plain {@link Intent} that bundles the given
 * customizations and carries its own metadata. Because it is just an intent,
 * it applies wherever intents are consumed (`uses={[theme]}`) and composes with
 * other themes.
 *
 * `customizes` is derived from the intents, so it can never drift from what the
 * theme actually does.
 *
 * `EngineProvider themePolicy="allow-forced-only"` suppresses this theme unless
 * it is applied through `forceApplyThemes`. `themePolicy="block-all"` suppresses it
 * even when forced.
 */
export function createTheme(
  meta: {
    name: string;
    description: string;
    category?: string;
    internal?: boolean;
  },
  intents: Intent<unknown>[],
): Intent {
  return themeIntent(intents, {...meta, customizes: derive(intents)});
}
